import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TextField, Typography } from '@mui/material';
import DataTable from '../../component/DataTable';
import ModuleHeading from '../../component/ModuleHeading';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import { get } from '../../services/api-services';
import { StyledTab } from '../../component/StyledComponent';
import { formatFullDate } from '../../modules/ConvertDate';
import { useSearchParams } from 'react-router-dom';

const salesCache = {
    enrollments: {},
    incomes: {}
};

const Sales = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabIndex = parseInt(searchParams.get('tab') || '0', 10);
    const [search, setSearch] = useState('');
    const searchDebounceRef = useRef(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
    // Ref to always have latest paginationModel in callbacks (avoids stale closure)
    const paginationModelRef = useRef({ page: 0, pageSize: 15 });
    const [rowCount, setRowCount] = useState(0);
    const [enrollments, setEnrollments] = useState([]);
    const [incomeData, setIncomeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const containerRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(0);
    const fetchEnrollments = useCallback(async () => {
        let token = localStorage.getItem("accessToken");
        const page = paginationModel.page || 0;
        const pageSize = paginationModel.pageSize || 15;
        const cacheKey = `${page}-${pageSize}-${search}`;

        if (salesCache.enrollments[cacheKey]) {
            setEnrollments(salesCache.enrollments[cacheKey].data);
            setRowCount(salesCache.enrollments[cacheKey].total);
            setLoading(false);
        } else {
            setLoading(true);
        }

        const params = new URLSearchParams();
        params.append('page', page);
        params.append('pageSize', pageSize);
        if (search) params.append('search', search);

        const url = `/Payments/paginate?${params.toString()}&access_token=${token}`;
        const response = await get(url);
        if (response?.statusCode === 200) {
            const fetchedData = response?.data?.data || [];
            const fetchedTotal = response?.data?.total || 0;
            setEnrollments(fetchedData);
            setRowCount(fetchedTotal);
            salesCache.enrollments[cacheKey] = { data: fetchedData, total: fetchedTotal };
            setLoading(false);
        } else {
            console.error('fetchEnrollments failed', response);
        }
    }, [search, paginationModel.page, paginationModel.pageSize]);

    const fetchIncomeData = useCallback(async () => {
        let token = localStorage.getItem("accessToken");
        const page = paginationModel.page || 0;
        const pageSize = paginationModel.pageSize || 15;
        const cacheKey = `${page}-${pageSize}-${search}`;

        if (salesCache.incomes[cacheKey]) {
            setIncomeData(salesCache.incomes[cacheKey].data);
            setRowCount(salesCache.incomes[cacheKey].total);
            setLoading(false);
        } else {
            setLoading(true);
        }

        const params = new URLSearchParams();
        params.append('page', page);
        params.append('pageSize', pageSize);
        if (search) params.append('search', search);

        const url = `/UserPrograms/paginate?${params.toString()}&access_token=${token}`;
        const response = await get(url);
        if (response?.statusCode === 200) {
            const fetchedData = response?.data?.data || [];
            const fetchedTotal = response?.data?.total || 0;
            setIncomeData(fetchedData);
            setRowCount(fetchedTotal);
            salesCache.incomes[cacheKey] = { data: fetchedData, total: fetchedTotal };
            setLoading(false);
        } else {
            console.error('fetchIncomeData failed', response);
        }
    }, [search, paginationModel.page, paginationModel.pageSize]);
    const incomeColumns = [
        {
            field: 'no',
            headerName: 'No',
            minWidth: 60,
            flex: 0,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                if (params.id && params.api) {
                    const sortedRowIds = params.api.getSortedRowIds();
                    const rowIndex = sortedRowIds.indexOf(params.id);
                    return <div className='flex gap-2 items-center'>{paginationModel.page * paginationModel.pageSize + rowIndex + 1}</div>;
                }
                return null;
            }
        },
        {
            field: 'date',
            headerName: 'Date',
            minWidth: 120,
            flex: 1,
            priority: 'medium',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center truncate' >
                    {formatFullDate(params?.row?.createdAt)}

                </div>
            }
        },
        {
            field: 'users',
            headerName: 'Users',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center' >
                    {params?.row?.customer?.name}
                </div>

            }
        },
        {
            field: 'enrolled',
            headerName: 'Enrolled In',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center'  >
                    {params?.row?.invoice?.purchaseProgram?.title}

                </div>
            }

        },

        {
            field: 'dueamount',
            headerName: 'Amount',
            minWidth: 120,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center' >
                    ₹{params?.row?.invoice?.purchaseProgram?.price}
                </div>
            }
        },
    ];
    const enrollmentColumns = [
        {
            field: 'no',
            headerName: 'No',
            minWidth: 60,
            flex: 0,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                if (params.id && params.api) {
                    const sortedRowIds = params.api.getSortedRowIds();
                    const rowIndex = sortedRowIds.indexOf(params.id);
                    return <div className='flex gap-4 items-center'>{paginationModel.page * paginationModel.pageSize + rowIndex + 1}</div>;
                }
                return null;
            }

        },
        {
            field: 'date',
            headerName: 'Date',
            minWidth: 120,
            flex: 1,
            priority: 'medium',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-4  items-center truncate' >
                    {formatFullDate(params?.row?.paidOn || params?.row?.createdAt)}
                </div>
            }

        },
        {
            field: 'users',
            headerName: 'Users',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-4  items-center' >
                    {params?.row?.customer?.name}
                </div>

            }
        },
        {
            field: 'enrolled',
            headerName: 'Enrolled In',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-4  items-center'  >
                    {params?.row?.program?.title}

                </div>
            }

        },
        {
            field: 'paymentStatus',
            headerName: 'Payment Status',
            minWidth: 120,
            flex: 1,
            priority: 'low',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className={`flex gap-4 items-center font-bold text-green-500`} >
                    Paid
                </div>
            }
        },
        {
            field: 'dueamount',
            headerName: 'Amount',
            minWidth: 120,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-4  items-center' >
                    ₹{params?.row?.amount?.toFixed(2)}
                </div>
            }
        },
    ];


    const handleFilterChange = (event) => {
        setSearch(event.target.value);
        const updated = { ...paginationModelRef.current, page: 0 };
        paginationModelRef.current = updated;
        setPaginationModel(updated);
    };
    const handleChange = (event, newValue) => {
        const newParams = new URLSearchParams(searchParams);
        if (newValue === 0) newParams.delete('tab');
        else newParams.set('tab', String(newValue));
        setSearchParams(newParams, { replace: true });

        const updated = { page: 0, pageSize: 15 };
        paginationModelRef.current = updated;
        setPaginationModel(updated);
    };
    useEffect(() => {
        const updateMaxHeight = () => {
            const height = window.innerHeight - 220;
            setMaxHeight(height);
        };
        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        return () => {
            window.removeEventListener('resize', updateMaxHeight);
        };
    }, []);
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            fetchEnrollments();
            fetchIncomeData();
        }, 500);
        return () => clearTimeout(searchDebounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        fetchEnrollments();
        fetchIncomeData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIndex, paginationModel.page, paginationModel.pageSize]);
    return (
        <div className='flex-1 flex flex-col space-y-4 min-h-0'>
            <ModuleHeading title="Sales" navigateTo="/admin/dashboard" />
            <Tabs
                value={tabIndex}
                onChange={handleChange}
                aria-label="wrapped label tabs example"
            >
                <StyledTab value={0} label="Enrollments" />
                <StyledTab value={1} label="Income" />
            </Tabs>
            <Box ref={containerRef} className="flex flex-1 flex-col mt-2 min-h-0" >
                <DataTable
                    headerContent={ 
                        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 md:items-center w-full justify-between">
                            <Typography variant='h6'>
                                {tabIndex === 0 ? 'Enrollments' : 'Income'}
                            </Typography>
                            <div className="flex space-x-4 md:items-center">
                                <TextField
                                    sx={{ minWidth: '250px' }}
                                    id="standard-basic"
                                    label="Search"
                                    variant="standard"
                                    value={search}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                    }
                    rows={tabIndex === 0 ? enrollments : incomeData}
                    columns={tabIndex === 0 ? enrollmentColumns : incomeColumns}
                    loading={loading}
                    tableId={tabIndex === 0 ? 'sales_enrollments' : 'sales_income'}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(newModel) => {
                        const current = paginationModelRef.current; // always fresh value via ref
                        if (newModel.pageSize !== current.pageSize) {
                            // Keep first visible row in view: find which page it falls on with new size
                            const firstVisibleItem = current.page * current.pageSize;
                            const newPage = Math.floor(firstVisibleItem / newModel.pageSize);
                            const updated = { page: newPage, pageSize: newModel.pageSize };
                            paginationModelRef.current = updated;
                            setPaginationModel(updated);
                        } else {
                            paginationModelRef.current = newModel;
                            setPaginationModel(newModel);
                        }
                    }}
                    paginationMode="server"
                    emptyMessage={tabIndex === 0 ? 'No enrollments found' : 'No income records found'}
                    emptySubMessage="Try adjusting your search criteria"
                />
            </Box>
        </div>
    );
};

export default Sales;


